import React, { useState } from 'react';
import './FAQSection.css';

export const faqItems = [
  {
    question: "Do you work in Next.js + Python (FastAPI/Django) or only Python?",
    answer: "Both, end to end. We default to Next.js 16 on the front, FastAPI or Django on the back, Celery + Redis for async work, and Postgres + pgvector for data and embeddings. We pick FastAPI for greenfield AI APIs and Django when admin gravity and a mature ORM save weeks. We adapt the stack to your codebase and constraints — not the other way around."
  },
  {
    question: "How do you de-risk AI features in regulated industries?",
    answer: "We build an eval set before we ship anything: real prompts, golden answers, scoring rubrics, and a CI job that runs them on every change. Production calls are traced through LangSmith or Langfuse. We add prompt-injection defenses, output validators, audit logging, and human-in-the-loop gates where the workflow demands it. Compliance posture (GDPR, HIPAA, SOC 2) is designed in, not retrofitted."
  },
  {
    question: "What's a typical engagement size and timeline?",
    answer: "Most engagements are 6–12 weeks. A focused AI MVP (Next.js + FastAPI + RAG, deployed) lands in 6–8 weeks. An agentic workflow over an internal corpus runs 6–10 weeks including the eval harness. A pure Python platform engagement (FastAPI, Celery, async SQLAlchemy) is usually 4–8 weeks. We cut scope deliberately rather than slip dates."
  },
  {
    question: "Do you offer fixed-price or T&M?",
    answer: "Both, depending on scope clarity. Fixed-price for tightly scoped MVPs and discrete platform builds where we can write down the deliverable. T&M for ongoing iteration, embedded teams, and exploratory AI work where the answer changes the next week's plan. Retainers are available for post-launch iteration."
  },
  {
    question: "Can you embed inside our existing engineering team?",
    answer: "Yes — that is a common engagement shape. We read your conventions, match your auth and routing patterns, and ship feature branches your team can review like any other. We do not impose our reference stack on your repo; we adapt to it where it matters and push back where the existing setup will block the AI feature."
  },
  {
    question: "How do you handle vector DBs — managed (Pinecone) or self-hosted (pgvector)?",
    answer: "We start with Postgres + pgvector unless we already know we need otherwise. You operate Postgres, your ops team understands it, and HNSW indexes get most teams to ~50M chunks before the second system earns its keep. We migrate to Pinecone (or Vectorize, or Weaviate) when filtering complexity, multi-tenant namespaces, or write throughput justify the operational cost."
  },
  {
    question: "What does an MVP timeline look like for an AI product?",
    answer: "Six to twelve weeks from empty repo to a deployed product. Week one is scope and the eval set. Weeks two and three stand up the reference architecture (Next.js + FastAPI + Celery + Postgres + pgvector + NextAuth) on a real preview URL. Weeks four through eight are the AI surface — SSE streaming, vector retrieval, tool calls, observability. The final week or two is hardening, runbook, and handoff."
  },
  {
    question: "Do you provide ongoing support after launch?",
    answer: "Yes — monthly retainers, T&M support, or fixed-scope iteration sprints. Every project includes a 30-day warranty on the delivered scope. Support covers model and dependency updates, eval re-runs against new GPT or Claude releases, prompt and retrieval tuning, and feature work."
  },
  {
    question: "Do you build with AI tools, and is that safe for our codebase?",
    answer: "Yes, on every engagement. Claude Code, Cursor and background PR agents work in your repo under a written spec and your conventions, on feature branches you can review like any other. Nothing merges without tests, LLM evals where relevant, and review by a named senior engineer. We pick models per task (Claude Opus 5.5 for architecture and hard refactors, Sonnet 5 and Haiku 4.5 for everyday work, open-weights models on vLLM when data must stay in-region), and we never point an agent at production data, auth or migrations without a human driving. The result is shorter cycle time with the same quality bar, and a codebase your team can keep working in."
  },
  {
    question: "Where are you based and how do you handle time zones?",
    answer: "Europe (Serbia). Our working hours overlap fully with EU clients and cover the morning of US East Coast and most of US West Coast. We default to written async (Linear, Slack, GitHub) with weekly demos and one synchronous review. GDPR-compliant by location and contract, USA-level engineering at European pricing."
  }
];

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section id="faq" className="faq-section section">
      <div className="container">
        <div className="section-header">
          <div className="section-badge">
            <span className="badge-icon">?</span>
            Buyer questions
          </div>
          <h2 className="section-title">
            What CTOs ask us <span className="logo-highlight">first</span>
          </h2>
          <p className="section-subtitle">
            Stack, scope, timeline, and the rest of the questions that show up in the first 30 minutes of a call.
          </p>
        </div>

        <div className="faq-list">
          {faqItems.map((faq, index) => (
            <div key={index} className={`faq-item ${openFAQ === index ? 'open' : ''}`}>
              <button className="faq-question" onClick={() => toggleFAQ(index)}>
                <span>{faq.question}</span>
                <svg className="faq-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
